import axios from "axios";

// Mirrors the endpoint resolution used in Redux/AdminReducer/action.js
const getEndpoint = () =>
  window.location.protocol === "http:"
    ? "http://localhost:5001"
    : `https://${window.location.host}/api`;

const authHeaders = () => {
  try {
    const userData = JSON.parse(sessionStorage.getItem("clinic-application-jwt"));
    return userData?.token ? { Authorization: `Bearer ${userData.token}` } : {};
  } catch (e) {
    return {};
  }
};

/** Staff: paginated shipment list with status counts. */
export const fetchShipments = async (params = {}) => {
  const { data } = await axios.get(`${getEndpoint()}/courier`, {
    headers: authHeaders(),
    params,
  });
  return data;
};

export const fetchShipment = async (id) => {
  const { data } = await axios.get(`${getEndpoint()}/courier/${id}`, {
    headers: authHeaders(),
  });
  return data;
};

/** Staff: create a shipment. Pass `book: true` to send it to DTDC at once. */
export const createShipment = async (payload) => {
  const { data } = await axios.post(`${getEndpoint()}/courier`, payload, {
    headers: authHeaders(),
  });
  return data;
};

export const updateShipment = async (id, payload) => {
  const { data } = await axios.put(`${getEndpoint()}/courier/${id}`, payload, {
    headers: authHeaders(),
  });
  return data;
};

/** Staff: push an existing draft to DTDC and get the consignment number. */
export const bookShipment = async (id) => {
  const { data } = await axios.post(`${getEndpoint()}/courier/${id}/book`, {}, {
    headers: authHeaders(),
  });
  return data;
};

/** Staff: live track one consignment against DTDC. */
export const trackShipment = async (id) => {
  const { data } = await axios.post(`${getEndpoint()}/courier/${id}/track`, {}, {
    headers: authHeaders(),
  });
  return data;
};

/** Staff: refresh every shipment that is still in motion. */
export const refreshAllShipments = async (limit = 25) => {
  const { data } = await axios.post(`${getEndpoint()}/courier/refresh`, {}, {
    headers: authHeaders(),
    params: { limit },
  });
  return data;
};

/** Staff: create shipments for courier details typed on prescriptions before the sync existed. */
export const backfillFromPrescriptions = async () => {
  const { data } = await axios.post(`${getEndpoint()}/courier/backfill-from-prescriptions`, {}, {
    headers: authHeaders(),
  });
  return data?.data || {};
};

export const cancelShipment = async (id, reason) => {
  const { data } = await axios.post(`${getEndpoint()}/courier/${id}/cancel`, { reason }, {
    headers: authHeaders(),
  });
  return data;
};

export const deleteShipment = async (id) => {
  const { data } = await axios.delete(`${getEndpoint()}/courier/${id}`, {
    headers: authHeaders(),
  });
  return data;
};

/** Staff: Courier account settings for this clinic (DTDC / India Post). Secrets come back masked. */
export const fetchCourierSettings = async (params = {}) => {
  const { data } = await axios.get(`${getEndpoint()}/courier/settings`, {
    headers: authHeaders(),
    params,
  });
  return data;
};

export const saveCourierSettings = async (payload) => {
  const { data } = await axios.put(`${getEndpoint()}/courier/settings`, payload, {
    headers: authHeaders(),
  });
  return data;
};

/** Patient: public tracking, no login. */
export const fetchPublicTracking = async (trackingToken) => {
  const { data } = await axios.get(`${getEndpoint()}/courier/public/track/${trackingToken}`);
  return data;
};

export const buildPatientTrackingLink = (trackingToken) =>
  `${window.location.origin}/track/${trackingToken}`;

/** Direct tracking pages on the official courier website */
export const buildDtdcTrackingLink = (consignmentNumber) =>
  `https://www.dtdc.in/tracking/shipment-tracking.asp?strCnno=${encodeURIComponent(consignmentNumber || '')}`;

export const buildIndiaPostTrackingLink = (consignmentNumber) =>
  `https://www.indiapost.gov.in/_layouts/15/dop.portal.tracking/trackconsignment.aspx`;

export const buildCourierTrackingLink = (provider, consignmentNumber) => {
  if (/india\s*post/i.test(provider || '') || /speed\s*post/i.test(provider || '')) {
    return buildIndiaPostTrackingLink(consignmentNumber);
  }
  return buildDtdcTrackingLink(consignmentNumber);
};
